import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  addPortfolioReviewLine,
  createPortfolioReview,
  deletePortfolioReview,
  deletePortfolioReviewLine,
  finalizePortfolioReview,
  getExternalHoldings,
  getPoorlyPerformingFunds,
  getPortfolioExposure,
  getPortfolioJourney,
  getPortfolioReview,
  getPortfolioReviewFrozenOverview,
  getPortfolioReviewInsights,
  getPortfolioReviewOverview,
  getPortfolioReviewReport,
  getPortfolioReviews,
  getReviewBenchmarks,
  getSwitchCandidates,
  updatePortfolioReviewLine,
  updatePortfolioReviewLines,
} from "../api/portfolio-review";
import {
  PortfolioJourneyParams,
  PortfolioReview,
  PortfolioReviewAccountParams,
  PortfolioReviewLineMutationResponse,
  PortfolioReviewListParams,
  PortfolioReviewOverviewParams,
} from "../types";

type QueryToggle = { enabled?: boolean };

// Landing page diagnostics

export const portfolioReviewOverviewQueryOptions = (
  variables: PortfolioReviewOverviewParams,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", "overview", variables],
    queryFn: () => getPortfolioReviewOverview(variables),
    placeholderData: keepPreviousData,
    enabled: !!variables.investment_account_guid && enabled,
  });

export function usePortfolioReviewOverview(
  variables: PortfolioReviewOverviewParams,
  options?: QueryToggle
) {
  return useQuery(portfolioReviewOverviewQueryOptions(variables, options));
}

export const poorlyPerformingFundsQueryOptions = (
  variables: PortfolioReviewAccountParams,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", "poorly-performing", variables],
    queryFn: () => getPoorlyPerformingFunds(variables),
    placeholderData: keepPreviousData,
    enabled: !!variables.investment_account_guid && enabled,
  });

export function usePoorlyPerformingFunds(
  variables: PortfolioReviewAccountParams,
  options?: QueryToggle
) {
  return useQuery(poorlyPerformingFundsQueryOptions(variables, options));
}

export const portfolioExposureQueryOptions = (
  variables: PortfolioReviewAccountParams,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", "exposure", variables],
    queryFn: () => getPortfolioExposure(variables),
    placeholderData: keepPreviousData,
    enabled: !!variables.investment_account_guid && enabled,
  });

export function usePortfolioExposure(
  variables: PortfolioReviewAccountParams,
  options?: QueryToggle
) {
  return useQuery(portfolioExposureQueryOptions(variables, options));
}

export const externalHoldingsQueryOptions = (
  investmentAccountId: string,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", "external-holdings", investmentAccountId],
    queryFn: () =>
      getExternalHoldings({ investment_account_guid: investmentAccountId }),
    enabled: !!investmentAccountId && enabled,
  });

export function useExternalHoldings(
  investmentAccountId: string,
  options?: QueryToggle
) {
  return useQuery(externalHoldingsQueryOptions(investmentAccountId, options));
}

export const portfolioJourneyQueryOptions = (
  variables: PortfolioJourneyParams,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", "journey", variables],
    queryFn: () => getPortfolioJourney(variables),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    enabled: !!variables.investment_account_guid && enabled,
  });

export function usePortfolioJourney(
  variables: PortfolioJourneyParams,
  options?: QueryToggle
) {
  return useQuery(portfolioJourneyQueryOptions(variables, options));
}

export const reviewBenchmarksQueryOptions = () =>
  queryOptions({
    queryKey: ["portfolio-review", "benchmarks"],
    queryFn: getReviewBenchmarks,
    staleTime: Infinity,
  });

export function useReviewBenchmarks() {
  return useQuery(reviewBenchmarksQueryOptions());
}

// Reviews

export const portfolioReviewsQueryOptions = (
  variables: PortfolioReviewListParams,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-reviews", variables],
    queryFn: () => getPortfolioReviews(variables),
    placeholderData: keepPreviousData,
    enabled,
  });

export function usePortfolioReviews(
  variables: PortfolioReviewListParams,
  options?: QueryToggle
) {
  return useQuery(portfolioReviewsQueryOptions(variables, options));
}

export const portfolioReviewQueryOptions = (
  reviewId: number,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", { reviewId }],
    queryFn: () => getPortfolioReview(reviewId),
    enabled: !!reviewId && enabled,
  });

export function usePortfolioReview(reviewId: number, options?: QueryToggle) {
  return useQuery(portfolioReviewQueryOptions(reviewId, options));
}

export const portfolioReviewInsightsQueryOptions = (
  reviewId: number,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", { reviewId }, "insights"],
    queryFn: () => getPortfolioReviewInsights(reviewId),
    enabled: !!reviewId && enabled,
  });

export function usePortfolioReviewInsights(
  reviewId: number,
  options?: QueryToggle
) {
  return useQuery(portfolioReviewInsightsQueryOptions(reviewId, options));
}

export const portfolioReviewFrozenOverviewQueryOptions = (
  reviewId: number,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", { reviewId }, "overview"],
    queryFn: () => getPortfolioReviewFrozenOverview(reviewId),
    enabled: !!reviewId && enabled,
  });

export function usePortfolioReviewFrozenOverview(
  reviewId: number,
  options?: QueryToggle
) {
  return useQuery(portfolioReviewFrozenOverviewQueryOptions(reviewId, options));
}

export const portfolioReviewReportQueryOptions = (
  reviewId: number,
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", { reviewId }, "report"],
    queryFn: () => getPortfolioReviewReport(reviewId),
    enabled: !!reviewId && enabled,
  });

export function usePortfolioReviewReport(
  reviewId: number,
  options?: QueryToggle
) {
  return useQuery(portfolioReviewReportQueryOptions(reviewId, options));
}

export const switchCandidatesQueryOptions = (
  variables: Parameters<typeof getSwitchCandidates>[0],
  { enabled = true }: QueryToggle = {}
) =>
  queryOptions({
    queryKey: ["portfolio-review", "switch-candidates", variables],
    queryFn: () => getSwitchCandidates(variables),
    placeholderData: keepPreviousData,
    enabled: !!variables.reviewId && !!variables.lineId && enabled,
  });

export function useSwitchCandidates(
  variables: Parameters<typeof getSwitchCandidates>[0],
  options?: QueryToggle
) {
  return useQuery(switchCandidatesQueryOptions(variables, options));
}

// Mutations

export function useCreatePortfolioReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPortfolioReview,
    onSuccess: (review) => {
      queryClient.setQueryData(
        portfolioReviewQueryOptions(review.id).queryKey,
        review
      );
      queryClient.invalidateQueries({ queryKey: ["portfolio-reviews"] });
    },
  });
}

export function useDeletePortfolioReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePortfolioReview,
    onSuccess: (_, reviewId) => {
      queryClient.removeQueries({
        queryKey: ["portfolio-review", { reviewId }],
      });
      queryClient.invalidateQueries({ queryKey: ["portfolio-reviews"] });
    },
  });
}

export function useFinalizePortfolioReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: finalizePortfolioReview,
    onSuccess: (review) => {
      queryClient.setQueryData(
        portfolioReviewQueryOptions(review.id).queryKey,
        review
      );
      queryClient.invalidateQueries({
        queryKey: ["portfolio-review", { reviewId: review.id }],
      });
      queryClient.invalidateQueries({ queryKey: ["portfolio-reviews"] });
    },
  });
}

// Line mutations return the recomputed insights, so the review in cache is
// patched in place instead of refetching every line.
function useLineMutationSync() {
  const queryClient = useQueryClient();
  return (reviewId: number, response: PortfolioReviewLineMutationResponse) => {
    queryClient.setQueryData(
      portfolioReviewInsightsQueryOptions(reviewId).queryKey,
      response.insights
    );
    queryClient.setQueryData(
      portfolioReviewQueryOptions(reviewId).queryKey,
      (review: PortfolioReview | undefined) => {
        if (!review) return review;
        const { line } = response;
        let { lines } = review;
        if (line) {
          lines = lines.some((l) => l.id === line.id)
            ? lines.map((l) => (l.id === line.id ? line : l))
            : [...lines, line];
        }
        return { ...review, lines, insights: response.insights };
      }
    );
    queryClient.invalidateQueries({ queryKey: ["portfolio-reviews"] });
  };
}

export function useAddPortfolioReviewLine() {
  const sync = useLineMutationSync();
  return useMutation({
    mutationFn: addPortfolioReviewLine,
    onSuccess: (response, { reviewId }) => sync(reviewId, response),
  });
}

export function useUpdatePortfolioReviewLine() {
  const sync = useLineMutationSync();
  return useMutation({
    mutationFn: updatePortfolioReviewLine,
    onSuccess: (response, { reviewId }) => sync(reviewId, response),
  });
}

export function useUpdatePortfolioReviewLines() {
  const queryClient = useQueryClient();
  const sync = useLineMutationSync();
  return useMutation({
    mutationFn: updatePortfolioReviewLines,
    onSuccess: (response, { reviewId }) => {
      sync(reviewId, response);
      // Bulk responses carry no line, so the lines themselves are refetched.
      queryClient.invalidateQueries({
        queryKey: portfolioReviewQueryOptions(reviewId).queryKey,
        exact: true,
      });
    },
  });
}

export function useDeletePortfolioReviewLine() {
  const queryClient = useQueryClient();
  const sync = useLineMutationSync();
  return useMutation({
    mutationFn: deletePortfolioReviewLine,
    onSuccess: (response, { reviewId, lineId }) => {
      sync(reviewId, response);
      queryClient.setQueryData(
        portfolioReviewQueryOptions(reviewId).queryKey,
        (review: PortfolioReview | undefined) =>
          review && {
            ...review,
            lines: review.lines.filter((l) => l.id !== lineId),
          }
      );
    },
  });
}
